import { useState, useMemo, useEffect, useRef } from "react";
import MatchRow from "../matchRow/matchRow";
import { useIsMobileBreakpoint } from "../../../hooks/useIsMobileBreakpoint";
import "./matchesByPhase.sass";

export default function MatchesByPhase({ matches = [], updateScore }) {
  const [activeJornada, setActiveJornada] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollContainerRef = useRef(null);
  const isMobile = useIsMobileBreakpoint();

  // Obtener fecha actual (inicio del día actual)
  const todayStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  // Verificar si UNA JORNADA COMPLETA está finalizada (todos sus partidos tienen clarosports-finished-game)
  const isJornadaFullyFinished = (jornada) => {
    const jornadaMatches = matches.filter(m => m.jornada === jornada);
    
    if (jornadaMatches.length === 0) return false;
    
    // Retornar true si TODOS los partidos de la jornada están finalizados
    return jornadaMatches.every(match => match.status === 'clarosports-finished-game');
  };

  /**
   * Obtiene la fecha más reciente de los partidos de una jornada
   * @param {string} jornada - Número de jornada
   * @returns {Date|null} Fecha más reciente o null si no hay
   */
  const getLatestMatchDateInJornada = (jornada) => {
    const jornadaMatches = matches.filter(m => m.jornada === jornada);
    if (jornadaMatches.length === 0) return null;
    
    let latestDate = null;
    jornadaMatches.forEach(match => {
      const dateStr = match.utc_date || match.eventDate;
      if (dateStr) {
        const matchDate = new Date(dateStr);
        if (!isNaN(matchDate.getTime())) {
          if (!latestDate || matchDate > latestDate) {
            latestDate = matchDate;
          }
        }
      }
    });
    
    return latestDate;
  };

  /**
   * Obtiene la medianoche (00:00:00) del siguiente día
   * Ejemplo: si fecha es 3-Nov 20:00 → retorna 4-Nov 00:00:00
   * 
   * @param {Date} date - Fecha base
   * @returns {Date} Medianoche del siguiente día
   */
  const getNextDayMidnight = (date) => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);  // Siguiente día
    nextDay.setHours(0, 0, 0, 0);            // Medianoche (00:00:00)
    return nextDay;
  };

  /**
   * Verifica si una jornada debe ocultarse por antigüedad
   * Oculta si todos los partidos están finalizados y ya pasó la medianoche del siguiente día
   * 
   * Ejemplo: Último partido 3-Nov 20:00 → se oculta a partir de 4-Nov 00:00
   * 
   * @param {string} jornada - Número de jornada
   * @returns {boolean} true si debe ocultarse
   */
  const shouldHideJornadaByAge = (jornada) => {
    // Paso 1: ¿Todos los partidos están finalizados?
    if (!isJornadaFullyFinished(jornada)) return false;
    
    // Paso 2: Obtener fecha del último partido
    const lastMatchDate = getLatestMatchDateInJornada(jornada);
    if (!lastMatchDate) return false;
    
    // Paso 3: Calcular medianoche del siguiente día
    const cutoffTime = getNextDayMidnight(lastMatchDate);
    
    // Paso 4: Obtener hora actual
    const now = new Date();
    
    // Paso 5: ¿Ha pasado la medianoche?
    return now >= cutoffTime;
  };

  // Verificar si una jornada debe ser visible
  const isJornadaVisible = (jornada) => {
    const jornadaMatches = matches.filter(m => m.jornada === jornada);
    
    // Si no hay partidos en esta jornada, no mostrarla
    if (jornadaMatches.length === 0) return false;

    // Si la jornada debe ocultarse por antigüedad, no mostrarla
    if (shouldHideJornadaByAge(jornada)) return false;

    // Mostrar la jornada si tiene al menos un partido pendiente o fue recientemente finalizada
    return true;
  };

  const currentMatchday = useMemo(() => {
    return matches.find(m => m.current === true) || null;
  }, [matches]);

  // Extraer jornadas únicas, ordenarlas y filtrar por visibilidad
  const jornadas = useMemo(() => {
    if (!Array.isArray(matches) || matches.length === 0) return [];
    
    const uniqueJornadas = [...new Set(matches.map(m => m.jornada).filter(Boolean))];
    
    // Ordenar con jornada 7 entre 16 y 17
    uniqueJornadas.sort((a, b) => {
      const numA = parseInt(a);
      const numB = parseInt(b);
      
      // Casos especiales para posicionar 7 entre 16 y 17
      if (numA === 7 && numB < 16) return 1;  // 7 va después de 1-15
      if (numA < 16 && numB === 7) return -1;  // 1-15 van antes de 7
      
      if (numA === 7 && numB > 16) return -1;  // 7 va antes de 17+
      if (numA > 16 && numB === 7) return 1;   // 17+ van después de 7
      
      if (numA === 16 && numB === 7) return -1;  // 16 va antes de 7
      if (numA === 7 && numB === 16) return 1;   // 7 va después de 16
      
      // Ordenamiento normal para el resto
      return numA - numB;
    });
    
    // Filtrar jornadas visibles
    const visibleJornadas = uniqueJornadas.filter(isJornadaVisible);
        
    return visibleJornadas;
  }, [matches, todayStart]);

  // Filtrar partidos visibles de la jornada activa
  const matchesActuales = useMemo(() => {
    if (!activeJornada) return [];
    return matches
      .filter(m => m.jornada === activeJornada)
  }, [activeJornada, matches, todayStart]);

  useEffect(() => {
    // Establecer la primera jornada visible como activa por defecto
    if (jornadas.length > 0 && !activeJornada) {
      setActiveJornada(jornadas[0]);
    }
  }, [jornadas, activeJornada]);

  // Verificar estado de scroll
  const updateScrollButtons = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 10
    );
  };

  useEffect(() => {
    updateScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
      
      // Recalcular después de que el DOM se haya renderizado completamente
      requestAnimationFrame(() => {
        updateScrollButtons();
      });
      
      return () => {
        container.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, [jornadas]);

  const handleJornadaChange = (newJornada) => {
    setActiveJornada(newJornada);
  };

  const handleScroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const scrollAmount = 200;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  if (!Array.isArray(matches) || matches.length === 0) {
    return (
      <div className="MatchesByPhase empty">
        <p>No hay partidos disponibles</p>
      </div>
    );
  }

  if (jornadas.length === 0) {
    return (
      <div className="MatchesByPhase empty">
        <p>No hay jornadas disponibles en este momento</p>
      </div>
    );
  }

  return (
    <div className="MatchesByPhase">
      <div className="JornadasContainer">
        {!isMobile && (
          <button 
            className={`ScrollButton Left ${!canScrollLeft ? 'disabled' : ''}`}
            onClick={() => handleScroll('left')}
            disabled={!canScrollLeft}
          >
          </button>
        )}

        <div className="JornadasScroll" ref={scrollContainerRef}>
          {jornadas.map((jornada) => {
            const isCurrentJornada = currentMatchday && currentMatchday.jornada === jornada;
            return (
              <button
                key={jornada}
                className={`JornadaTab ${activeJornada === jornada ? 'active' : ''} ${isCurrentJornada ? 'current' : ''}`}
                onClick={() => handleJornadaChange(jornada)}
              >
                JORNADA {jornada}
              </button>
            );
          })}
        </div>

        {!isMobile && (
          <button 
            className={`ScrollButton Right ${!canScrollRight ? 'disabled' : ''}`}
            onClick={() => handleScroll('right')}
            disabled={!canScrollRight}
          >
          </button>
        )}
      </div>

      <div className="MatchesList">
        {matchesActuales.length > 0 ? (
          matchesActuales.map((match) => (
            <MatchRow
              key={match.id}
              match={match}
              updateScore={updateScore}
            />
          ))
        ) : (
          <div className="NoMatches">
            No hay partidos disponibles para esta jornada
          </div>
        )}
      </div>
    </div>
  );
}